import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import celImage from '../assets/story.jpg';
import ikeImage from '../assets/ike.jpg';
import bejalImage from '../assets/bejal.jpg';
import victorImage from '../assets/victor.jpg';

function AnimatedCounter({ end, duration = 1500, suffix = '', className }) {
  const [value, setValue] = React.useState(0);
  const ref = React.useRef(null);
  const startedRef = React.useRef(false);
  React.useEffect(() => {
    const animate = () => {
      const start = performance.now();
      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const current = Math.floor(progress * end);
        setValue(current);
        if (progress < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !startedRef.current) {
            startedRef.current = true;
            animate();
          }
        });
      },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);
  return <p ref={ref} className={className}>{value.toLocaleString()}{'' + suffix}</p>;
}

const About = () => {
  return (
    <div>
      <Navbar />

      {/* Page header */}
      <div className="hero-section">
        <h1 className="text-3xl sm:text-4xl md:text-5xl text-white font-bold mb-4">About DeafCanTalk</h1>
        <p className="text-base sm:text-lg md:text-xl text-white">Our mission is to empower the deaf and hard-of-hearing community</p>
      </div>

      {/* Mission section */}
      <div className="section">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#003366] mb-6 text-center">Our Mission</h2>
        <p className="text-base sm:text-lg text-gray-700 leading-relaxed mb-4">
          DeafCanTalk was founded with a simple yet powerful vision: to create a world where communication barriers no longer exist for the deaf and hard-of-hearing community. We believe that everyone deserves to be heard, understood, and included in every conversation.
        </p>
        <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
          Our mobile app leverages cutting-edge technology to provide real-time sign language translation, educational resources, and a supportive community platform. We are committed to breaking down barriers and fostering a more inclusive society where deaf individuals can thrive.
        </p>
      </div>

      {/* History section */}
      <div className="section bg-gray-50">
        <div className="w-full flex flex-col md:flex-row items-center gap-8">
          <div className="w-full md:w-1/2">
            <img
              src={celImage}
              alt="Our Story"
              className="rounded-lg shadow-lg w-full h-auto object-cover max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg mx-auto"
            />
          </div>
          <div className="w-full md:w-1/2 flex flex-col gap-4">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#003366]">Our Story</h2>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              DeafCanTalk began in 2020 when a group of passionate developers, educators, and deaf community advocates came together with a shared goal: to revolutionize communication for the deaf and hard-of-hearing.
            </p>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              What started as a small project has grown into a comprehensive platform serving thousands of users worldwide. Our team has worked tirelessly to develop innovative features that truly meet the needs of our community.
            </p>
          </div>
        </div>
      </div>

      {/* Vision section */}
      <div className="section">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#003366] mb-6 text-center">Our Vision</h2>
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          {/* Vision 1 */}
          <div className="card w-full">
            <h3 className="text-xl sm:text-2xl font-semibold text-[#0066cc] mb-3">Global Accessibility</h3>
            <p className="text-gray-700 text-sm sm:text-base">
              We envision a world where sign language is universally recognized and accessible technology empowers every deaf individual to communicate effortlessly.
            </p>
          </div>

          {/* Vision 2 */}
          <div className="card w-full">
            <h3 className="text-xl sm:text-2xl font-semibold text-[#0066cc] mb-3">Education for All</h3>
            <p className="text-gray-700 text-sm sm:text-base">
              We aim to provide comprehensive educational resources in sign language with voice overlays for an inclusive education.
            </p>
          </div>

          {/* Vision 3 */}
          <div className="card w-full">
            <h3 className="text-xl sm:text-2xl font-semibold text-[#0066cc] mb-3">Innovation & Advocacy</h3>
            <p className="text-gray-700 text-sm sm:text-base">
              We are committed to continuous innovation and advocacy, pushing for policies and technologies that promote inclusiveness and equality.
            </p>
          </div>
        </div>
      </div>

      {/* Team section */}
      <div className="section bg-gray-50 w-full">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#003366] mb-6 text-center">Meet Our Team</h2>
        <p className="text-base sm:text-lg text-gray-700 text-center mb-8">
          Our diverse team brings together expertise in technology, education, and deaf advocacy to create meaningful impact.
        </p>
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 justify-center">
          {/* Team member 1 */}
          <div className="card w-full text-center">
            <img src={ikeImage} alt="Ike Agyei Menah" className="w-full h-auto rounded-lg object-cover" />
            <h3 className="text-lg sm:text-xl font-bold text-[#003366] mb-2">Ike Agyei Menah</h3>
            <p className="text-sm sm:text-md text-[#0066cc] mb-3">Founder & CEO</p>
            <p className="text-sm text-gray-700">
              Passionate advocate for deaf rights with 15 years of experience in accessibility technology.
            </p>
          </div>

          {/* Team member 2 */}
          <div className="card w-full text-center">
            <img src={bejalImage} alt="Bejal Joshi" className="w-full h-auto rounded-lg object-cover" />
            <h3 className="text-lg sm:text-xl font-bold text-[#003366] mb-2">Bejal Joshi</h3>
            <p className="text-sm sm:text-md text-[#0066cc] mb-3">COO & Cofounder</p>
            <p className="text-sm text-gray-700">
              Tech Enthusiast with strong strategist with 10 years operational expertise.
            </p>
          </div>

          {/* Team member 3 */}
          <div className="card w-full text-center">
            <img src={victorImage} alt="Victor Wealth-Adankai" className="w-full h-auto rounded-lg object-cover" />
            <h3 className="text-lg sm:text-xl font-bold text-[#003366] mb-2">Victor Wealth-Adankai</h3>
            <p className="text-sm sm:text-md text-[#0066cc] mb-3">CTO & Cofounder</p>
            <p className="text-sm text-gray-700">
              An AI Machine Learning expert with 10 years experience.

            </p>
          </div>
        </div>
      </div>

      {/* Community impact */}
      <div className="section">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#003366] mb-6 text-center">Our Community Impact</h2>
        <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 items-start">
          <div className="card w-full text-center">
            <AnimatedCounter end={20000} suffix="+" className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-500 mb-2" />
            <p className="text-sm sm:text-base text-gray-700">Active Users</p>
          </div>

          <div className="card w-full text-center">
            <AnimatedCounter end={100} suffix="+" className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-600 mb-2" />
            <p className="text-sm sm:text-base text-gray-700">Educational Resources</p>
          </div>

          <div className="card w-full text-center">
            <AnimatedCounter end={25} className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-800 mb-2" />
            <p className="text-sm sm:text-base text-gray-700">Countries Targeted</p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default About;
